import { useMemo } from "react";

import type { TrailType } from "~/v2/constants/equityOrder";

interface UseTrailingStopPriceProps {
  trailType: TrailType;
  trailValue: string | number | undefined;
  lastQuotePrice: number | undefined;
}

export interface TrailingStopPrice {
  buy: string;
  sell: string;
}

const formatCurrency = (value: number): string =>
  value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: "currency",
    currency: "USD",
  });

/**
 * Calculates the trailing stop trigger prices for both buy and sell orders.
 * - Buy stop: triggers when price rises above (lastQuotePrice + trail)
 * - Sell stop: triggers when price drops below (lastQuotePrice - trail)
 */
export const getTrailingStopPrices = ({
  trailType,
  trailValue,
  lastQuotePrice,
}: UseTrailingStopPriceProps): TrailingStopPrice => {
  if (!lastQuotePrice || !trailValue || Number.isNaN(Number(trailValue))) {
    return { buy: "-", sell: "-" };
  }

  const trailValueNumber = Number(trailValue);
  const price = lastQuotePrice;

  if (trailType === "trail_percent") {
    return {
      buy: formatCurrency(price * (1 + trailValueNumber / 100)),
      sell: formatCurrency(price * (1 - trailValueNumber / 100)),
    };
  }

  return {
    buy: formatCurrency(price + trailValueNumber),
    sell: formatCurrency(price - trailValueNumber),
  };
};

export const useTrailingStopPrice = ({
  trailType,
  trailValue,
  lastQuotePrice,
}: UseTrailingStopPriceProps): TrailingStopPrice => {
  const trailingStopPrices = useMemo(
    () =>
      getTrailingStopPrices({
        trailType,
        trailValue,
        lastQuotePrice,
      }),
    [lastQuotePrice, trailType, trailValue],
  );

  return trailingStopPrices;
};
